import { useEffect, useState } from "react";
import { adminApi } from "../services/api";
import { useAuthStore } from "../stores/authStore";

const ALL_PERMISSIONS = [
  { key: "families:read",      label: "Search & view families" },
  { key: "families:edit",      label: "Edit family settings" },
  { key: "families:delete",    label: "Delete family" },
  { key: "kids:read",          label: "View kids + balances" },
  { key: "kids:pin_reset",     label: "Reset kid PIN" },
  { key: "kids:balance",       label: "Adjust star balance" },
  { key: "kids:delete",        label: "Delete kid" },
  { key: "completions:read",   label: "View completions + photos" },
  { key: "completions:delete", label: "Delete completion" },
  { key: "backup:export",      label: "Export family backup" },
  { key: "backup:restore",     label: "Restore family from backup" },
  { key: "metrics:read",       label: "View metrics & audit log" },
  { key: "team:invite",        label: "Invite team members" },
  { key: "team:revoke",        label: "Revoke team access" },
  { key: "roles:configure",    label: "Configure role permissions (Root only)" },
];

const ROLE_IDS   = ["admin", "support_l2", "support_l1"];
const ROLE_LABELS: Record<string, string> = { admin: "Admin", support_l2: "Support L2", support_l1: "Support L1" };
const ROLE_BADGES: Record<string, string> = { admin: "badge-admin", support_l2: "badge-l2", support_l1: "badge-l1" };

export default function Permissions() {
  const { role } = useAuthStore();
  const [roles,   setRoles]   = useState<Record<string, Set<string>>>({});
  const [dirty,   setDirty]   = useState<Record<string, boolean>>({});
  const [saving,  setSaving]  = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (role !== "root") {
      setLoading(false);
      return;
    }
    adminApi.getRoles().then((res) => {
      const map: Record<string, Set<string>> = {};
      for (const r of res.roles) {
        map[r.roleId] = new Set(Array.from(r.permissions as Iterable<string>));
      }
      setRoles(map);
    }).finally(() => setLoading(false));
  }, [role]);

  if (role !== "root") {
    return <div className="card" style={{ color: "var(--text-muted)" }}>Root access required.</div>;
  }

  function toggle(roleId: string, perm: string) {
    setRoles((prev) => {
      const s = new Set(prev[roleId] ?? []);
      s.has(perm) ? s.delete(perm) : s.add(perm);
      return { ...prev, [roleId]: s };
    });
    setDirty((prev) => ({ ...prev, [roleId]: true }));
  }

  async function save(roleId: string) {
    setSaving(roleId);
    try {
      await adminApi.updateRole(roleId, Array.from(roles[roleId] ?? []));
      setDirty((prev) => ({ ...prev, [roleId]: false }));
    } finally { setSaving(null); }
  }

  if (loading) return <div style={{ color: "var(--text-muted)" }}>Loading…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <h1 style={{ fontSize: 20, fontWeight: 900 }}>⚙️ Permissions</h1>
      <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Configure what each role can do. Changes take effect at next login.</div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "var(--surface2)" }}>
              <th style={{ padding: "10px 16px", textAlign: "left", color: "var(--text-muted)", fontSize: 10, textTransform: "uppercase" }}>Permission</th>
              <th style={{ padding: "10px 14px", textAlign: "center", color: "#fca5a5", fontSize: 11 }}>🔑 Root</th>
              {ROLE_IDS.map((r) => (
                <th key={r} style={{ padding: "10px 14px", textAlign: "center", fontSize: 11 }}>
                  <span className={`badge ${ROLE_BADGES[r]}`}>{ROLE_LABELS[r]}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ALL_PERMISSIONS.map((p) => (
              <tr key={p.key} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={{ padding: "9px 16px" }}>
                  {p.label}<br />
                  <span style={{ fontFamily: "monospace", fontSize: 10, color: "var(--text-muted)" }}>{p.key}</span>
                </td>
                <td style={{ textAlign: "center", padding: "9px 14px", color: "#fca5a5", fontWeight: 900 }}>●</td>
                {ROLE_IDS.map((r) => (
                  <td key={r} style={{ textAlign: "center", padding: "9px 14px" }}>
                    <input type="checkbox" checked={roles[r]?.has(p.key) ?? false}
                      onChange={() => toggle(r, p.key)}
                      style={{ width: 16, height: 16, cursor: "pointer", accentColor: "var(--accent)" }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        {ROLE_IDS.map((r) => (
          <button key={r} className="btn btn-primary" disabled={!dirty[r] || saving === r} onClick={() => save(r)}>
            {saving === r ? "Saving…" : `Save ${ROLE_LABELS[r]}`}
            {dirty[r] && saving !== r && <span style={{ marginLeft: 4, color: "#fcd34d" }}>●</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
